import { useState, useMemo, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { UNIT_IDS } from '../../constants/guideConstants';
import { formatDscItem } from '../../../../../helpers/dte/dscItemFormatter';
import { buildMortalityDetailDescriptionParts } from '../../utils/mortalityDscParts';
import { buildGlosasBlockForDscItem } from './residuosDscItem';
import { mergeStockSendGuideItems } from '../../utils/mergeWarehouseStockGuideItems';
import { useFormDictionary } from '../FormDictionaryContext';
import { mergeGuideDetailSendItems } from '../../utils/mergeGuideDetailSendItems';

/**
 * Hook que encapsula estado, flags y handlers de Residuos para ItemsSection.
 * Se llama incondicionalmente (reglas de hooks); la lógica solo aplica cuando guide_category === 'residuos'.
 */
export function useResiduosItems(validation, items) {
	const { findWarehouseById } = useFormDictionary();
	const [isResiduosModalOpen, setIsResiduosModalOpen] = useState(false);
	const [isBeachCleaningModalOpen, setIsBeachCleaningModalOpen] =
		useState(false);
	const [editingResidueDetail, setEditingResidueDetail] = useState(null);
	const [editingResidueIndex, setEditingResidueIndex] = useState(null);
	const [isWarehouseStockPickerOpen, setIsWarehouseStockPickerOpen] =
		useState(false);
	const [isGuideDetailPickerOpen, setIsGuideDetailPickerOpen] =
		useState(false);

	// --- Flags derivados ---

	const isResiduos = useMemo(() => {
		return validation.values.guide_category === 'residuos';
	}, [validation.values.guide_category]);

	const isLimpiezaPlaya = useMemo(() => {
		return (
			validation.values.guide_category === 'residuos' &&
			validation.values.residuos_operation_type === 'limpieza_playa'
		);
	}, [
		validation.values.guide_category,
		validation.values.residuos_operation_type,
	]);

	const isResiduosRegulares = useMemo(() => {
		return (
			validation.values.guide_category === 'residuos' &&
			(validation.values.residuos_operation_type === 'peligrosos' ||
				validation.values.residuos_operation_type === 'no_peligrosos')
		);
	}, [
		validation.values.guide_category,
		validation.values.residuos_operation_type,
	]);

	const isEnvioBodegaPeligrosos = useMemo(() => {
		return (
			validation.values.guide_category === 'residuos' &&
			validation.values.residuos_operation_type === 'envio_bodega_peligrosos'
		);
	}, [
		validation.values.guide_category,
		validation.values.residuos_operation_type,
	]);

	const isEnvioBodegaNoPeligrosos = useMemo(() => {
		return (
			validation.values.guide_category === 'residuos' &&
			validation.values.residuos_operation_type ===
				'envio_bodega_no_peligrosos'
		);
	}, [
		validation.values.guide_category,
		validation.values.residuos_operation_type,
	]);

	// --- Memos derivados de items ---

	const guideDetailsList = useMemo(
		() =>
			items
				.filter(item => item._module_vars?.residue_id)
				.map(item => item._module_vars),
		[items],
	);

	const currentResidueIds = useMemo(
		() =>
			items
				.filter(item => item._module_vars?.residue_id)
				.map(item => item._module_vars?.residue_id),
		[items],
	);

	const residueItemsCount = useMemo(
		() => items.filter(i => i._module_vars?.residue_id).length,
		[items],
	);

	// --- Effect: strip legacy auto items ---

	useEffect(() => {
		if (!isResiduos) return;
		const next = (items || []).filter(i => !i._module_vars?._is_auto_item);
		if (next.length === (items || []).length) return;
		validation.setFieldValue('items', next, false);
	}, [isResiduos, items, validation]);

	const openWarehouseStockPicker = useCallback(() => {
		setIsWarehouseStockPickerOpen(true);
	}, []);

	const closeWarehouseStockPicker = useCallback(() => {
		setIsWarehouseStockPickerOpen(false);
	}, []);

	const openGuideDetailPicker = useCallback(() => {
		setIsGuideDetailPickerOpen(true);
	}, []);

	const closeGuideDetailPicker = useCallback(() => {
		setIsGuideDetailPickerOpen(false);
	}, []);

	const handleConfirmGuideDetailPick = useCallback(
		linesFromModal => {
			const rawItems = Array.isArray(validation.values.items)
				? validation.values.items
				: [];
			const { items: next, stats } = mergeGuideDetailSendItems(
				rawItems,
				linesFromModal,
			);
			validation.setFieldValue('items', next, false);
			const parts = [];
			if (stats.added > 0) {
				parts.push(`${stats.added} residuo(s) nuevo(s)`);
			}
			if (stats.merged > 0) {
				parts.push(`${stats.merged} fusionado(s)`);
			}
			toast.success(
				parts.length > 0
					? `Líneas de guía agregadas: ${parts.join(', ')}.`
					: 'Líneas de guía actualizadas.',
			);
			setIsGuideDetailPickerOpen(false);
		},
		[validation],
	);

	const stockSendWarehouseName = useMemo(() => {
		const whId = validation.values.warehouse_origin_id;
		if (!whId) return '';
		return findWarehouseById(whId)?.name || '';
	}, [validation.values.warehouse_origin_id, findWarehouseById]);

	const handleConfirmWarehouseStockPick = useCallback(
		slotsFromModal => {
			const rawItems = Array.isArray(validation.values.items)
				? validation.values.items
				: [];
			const { items: next, stats } = mergeStockSendGuideItems(
				rawItems,
				slotsFromModal,
				{ warehouseName: stockSendWarehouseName },
			);
			validation.setFieldValue('items', next, false);
			const parts = [];
			if (stats.added > 0) {
				parts.push(
					`${stats.added} línea(s) nueva(s)`,
				);
			}
			if (stats.merged > 0) {
				parts.push(
					`${stats.merged} fusionada(s)`,
				);
			}
			toast.success(
				parts.length > 0
					? `Stock RESPEL agregado: ${parts.join(', ')}.`
					: 'Stock RESPEL actualizado.',
			);
			setIsWarehouseStockPickerOpen(false);
		},
		[validation, stockSendWarehouseName],
	);

	// --- Handlers ---

	const handleAddResidue = useCallback(
		async residueDetail => {
			const dscOptions = { establishmentTypeId: validation.values.establishment_type || '' };
			const mortalityFields =
				buildMortalityDetailDescriptionParts(residueDetail, dscOptions);
			let description =
				mortalityFields.length > 0 ? mortalityFields.join(', ') : '';

			const hasValorizableInExisting = items.some(
				item => item._module_vars?.es_valorizable,
			);
			const hasValorizable =
				hasValorizableInExisting || residueDetail.es_valorizable;
			const finalTransportIndex = hasValorizable ? 1 : validation.values.transport_index;

			const glosasBlock = buildGlosasBlockForDscItem(
				{ ...validation.values, transport_index: finalTransportIndex },
				residueDetail.residue_id,
			);
			if (glosasBlock) {
				description = description
					? `${description}\n\n${glosasBlock}`
					: glosasBlock;
			}

			if (description) {
				description = formatDscItem(description, { maxTotalLength: 1000 });
			}

		let calculatedPrice = 0;
		let calculatedAmount = 0;
		let conversionRateDetail = null;

		if (residueDetail.es_valorizable && residueDetail.valor) {
			try {
				const { convertToCLP, getCurrentDate } =
					await import('../../../../../helpers/currency/currency_helper');
				const { clpAmount, rateDetail } = await convertToCLP(
					residueDetail.valor,
					residueDetail.moneda || 'CLP',
					getCurrentDate(),
				);
				calculatedPrice = Math.round(clpAmount);
				calculatedAmount = Math.round(
					calculatedPrice * residueDetail.amount,
				);
				conversionRateDetail = rateDetail;
			} catch (error) {
				console.error('Error al convertir moneda:', error);
				toast.error('Error al calcular precio: ' + error.message);
			}
		}

		if (hasValorizable && validation.values.transport_index !== 1) {
			validation.setFieldValue('transport_index', 1, false);
		}

		const newItem = {
			NmbItem: residueDetail.residue?.name || 'Residuo',
			DscItem: description,
			QtyItem: residueDetail.amount,
			UnmdItem: residueDetail.unit_measurement?.code || 'KG',
			PrcItem: calculatedPrice,
			MontoItem: calculatedAmount,
			VlrCodigo: residueDetail.residue?.code || '',
			TpoCodigo: '',
			_module_vars: {
				residue_id: residueDetail.residue_id,
				residue: residueDetail.residue,
				unit_measurement_id: residueDetail.unit_measurement_id,
				unit_measurement: residueDetail.unit_measurement,
				residue_category_id: residueDetail.residue_category_id,
				residue_kilograms: residueDetail.residue_kilograms,
				residue_collected_kilograms:
					residueDetail.residue_collected_kilograms,
				residue_collected_ids: residueDetail.residue_collected_ids || [],
				kilograms_total: residueDetail.kilograms_total,
				ph_ensilage: residueDetail.ph_ensilage || null,
				hydrogen_sulfide: residueDetail.hydrogen_sulfide || null,
				number_of_bins: residueDetail.number_of_bins || null,
				especie: residueDetail.especie || null,
				fish_count: residueDetail.fish_count || null,
				cubic_meters: residueDetail.cubic_meters || null,
				has_flow_meter: residueDetail.has_flow_meter || null,
				period: residueDetail.period || null,
				cultivation_center_origin: residueDetail.cultivation_center_origin || null,
				siep_code_cultivation_center: residueDetail.siep_code_cultivation_center || null,
				cage_origin: residueDetail.cage_origin || null,
				bin_seal_folios: residueDetail.bin_seal_folios || null,
				market_restrictions: residueDetail.market_restrictions || null,
				es_valorizable: residueDetail.es_valorizable || false,
				valor: residueDetail.valor || null,
				moneda: residueDetail.moneda || null,
				valor_clp: calculatedPrice,
				conversion_rate: conversionRateDetail?.value || null,
				conversion_rate_date: conversionRateDetail?.date || null,
			},
		};

			const cleanedItems = items.filter(
				item => !item._module_vars?._is_auto_item,
			);
			const finalItems = [...cleanedItems, newItem];

			validation.setFieldValue('items', finalItems, false);
		},
		[items, validation],
	);

	const handleUpdateResidue = useCallback(
		async updatedDetail => {
			const dscOptions = { establishmentTypeId: validation.values.establishment_type || '' };
			const mortalityFields =
				buildMortalityDetailDescriptionParts(updatedDetail, dscOptions);
			let description =
				mortalityFields.length > 0 ? mortalityFields.join(', ') : '';

			const hasValorizableInOthers = items.some(
				(item, idx) =>
					idx !== editingResidueIndex &&
					item._module_vars?.es_valorizable,
			);
			const hasValorizable =
				hasValorizableInOthers || updatedDetail.es_valorizable;
			const finalTransportIndex = hasValorizable
				? 1
				: validation.values.transport_index;

			const glosasBlock = buildGlosasBlockForDscItem(
				{ ...validation.values, transport_index: finalTransportIndex },
				updatedDetail.residue_id,
			);
			if (glosasBlock) {
				description = description
					? `${description}\n\n${glosasBlock}`
					: glosasBlock;
			}

			if (description) {
				description = formatDscItem(description, { maxTotalLength: 1000 });
			}

		let calculatedPrice = 0;
		let calculatedAmount = 0;
		let conversionRateDetail = null;

		if (updatedDetail.es_valorizable && updatedDetail.valor) {
			try {
				const { convertToCLP, getCurrentDate } =
					await import('../../../../../helpers/currency/currency_helper');
				const { clpAmount, rateDetail } = await convertToCLP(
					updatedDetail.valor,
					updatedDetail.moneda || 'CLP',
					getCurrentDate(),
				);
				calculatedPrice = Math.round(clpAmount);
				calculatedAmount = Math.round(
					calculatedPrice * updatedDetail.amount,
				);
				conversionRateDetail = rateDetail;
			} catch (error) {
				console.error('Error al convertir moneda:', error);
				toast.error('Error al calcular precio: ' + error.message);
			}
		}

		if (hasValorizable && validation.values.transport_index !== 1) {
			validation.setFieldValue('transport_index', 1, false);
		}

		const updatedItem = {
			NmbItem: updatedDetail.residue?.name || 'Residuo',
			DscItem: description,
			QtyItem: updatedDetail.amount,
			UnmdItem: updatedDetail.unit_measurement?.code || 'KG',
			PrcItem: calculatedPrice,
			MontoItem: calculatedAmount,
			VlrCodigo: updatedDetail.residue?.code || '',
			TpoCodigo: '',
			_module_vars: {
				residue_id: updatedDetail.residue_id,
				residue: updatedDetail.residue,
				unit_measurement_id: updatedDetail.unit_measurement_id,
				unit_measurement: updatedDetail.unit_measurement,
				residue_category_id: updatedDetail.residue_category_id,
				residue_kilograms: updatedDetail.residue_kilograms,
				residue_collected_kilograms:
					updatedDetail.residue_collected_kilograms,
				residue_collected_ids: updatedDetail.residue_collected_ids || [],
				kilograms_total: updatedDetail.kilograms_total,
				ph_ensilage: updatedDetail.ph_ensilage || null,
				hydrogen_sulfide: updatedDetail.hydrogen_sulfide || null,
				number_of_bins: updatedDetail.number_of_bins || null,
				especie: updatedDetail.especie || null,
				fish_count: updatedDetail.fish_count || null,
				cubic_meters: updatedDetail.cubic_meters || null,
				has_flow_meter: updatedDetail.has_flow_meter || null,
				period: updatedDetail.period || null,
				cultivation_center_origin: updatedDetail.cultivation_center_origin || null,
				siep_code_cultivation_center: updatedDetail.siep_code_cultivation_center || null,
				cage_origin: updatedDetail.cage_origin || null,
				bin_seal_folios: updatedDetail.bin_seal_folios || null,
				market_restrictions: updatedDetail.market_restrictions || null,
				es_valorizable: updatedDetail.es_valorizable || false,
				valor: updatedDetail.valor || null,
				moneda: updatedDetail.moneda || null,
				valor_clp: calculatedPrice,
				conversion_rate: conversionRateDetail?.value || null,
				conversion_rate_date: conversionRateDetail?.date || null,
			},
		};

			const regularItems = items.filter(
				item => !item._module_vars?._is_auto_item,
			);
			const targetItem = items[editingResidueIndex];
			const updatedRegularItems = regularItems.map(item =>
				item === targetItem ? updatedItem : item,
			);

			validation.setFieldValue('items', updatedRegularItems);
			setEditingResidueDetail(null);
			setEditingResidueIndex(null);
		},
		[items, editingResidueIndex, validation],
	);

	const handleEditResidue = (item, index) => {
		const residueDetail = {
			residue_id: item._module_vars?.residue_id,
			residue: item._module_vars?.residue,
			residue_category_id: item._module_vars?.residue_category_id,
			unit_measurement_id: item._module_vars?.unit_measurement_id,
			unit_measurement: item._module_vars?.unit_measurement,
			amount: item.QtyItem,
			residue_kilograms: item._module_vars?.residue_kilograms,
			residue_collected_kilograms:
				item._module_vars?.residue_collected_kilograms || 0,
			residue_collected_ids: item._module_vars?.residue_collected_ids || [],
			kilograms_total: item._module_vars?.kilograms_total,
			ph_ensilage: item._module_vars?.ph_ensilage || null,
			hydrogen_sulfide: item._module_vars?.hydrogen_sulfide || null,
			number_of_bins: item._module_vars?.number_of_bins || null,
			especie: item._module_vars?.especie || null,
			fish_count: item._module_vars?.fish_count || null,
			cubic_meters: item._module_vars?.cubic_meters || null,
			has_flow_meter: item._module_vars?.has_flow_meter || null,
			period: item._module_vars?.period || null,
			cultivation_center_origin: item._module_vars?.cultivation_center_origin || null,
			siep_code_cultivation_center: item._module_vars?.siep_code_cultivation_center || null,
			cage_origin: item._module_vars?.cage_origin || null,
			bin_seal_folios: item._module_vars?.bin_seal_folios || null,
			market_restrictions: item._module_vars?.market_restrictions || null,
			es_valorizable: item._module_vars?.es_valorizable || false,
			valor: item._module_vars?.valor || null,
			moneda: item._module_vars?.moneda || null,
		};

		setEditingResidueDetail(residueDetail);
		setEditingResidueIndex(index);
		setIsResiduosModalOpen(true);
	};

	const handleCloseResiduosModal = () => {
		setIsResiduosModalOpen(false);
		setEditingResidueDetail(null);
		setEditingResidueIndex(null);
	};

	const handleCloseBeachCleaningModal = useCallback(() => {
		setIsBeachCleaningModalOpen(false);
	}, []);

	const handleBeachCleaningSelected = selectedCleanings => {
		if (!selectedCleanings || selectedCleanings.length === 0) {
			return;
		}

		const cleaningsByResidue = selectedCleanings.reduce((acc, cleaning) => {
			const residueId = cleaning.residue?._id;
			if (!residueId) return acc;

			if (!acc[residueId]) {
				acc[residueId] = {
					cleanings: [],
					residue: cleaning.residue,
				};
			}
			acc[residueId].cleanings.push(cleaning);
			return acc;
		}, {});

		let updatedItems = [...items];

		Object.keys(cleaningsByResidue).forEach(residueId => {
			const { cleanings, residue } = cleaningsByResidue[residueId];
			const cleaningKilograms = cleanings.reduce(
				(sum, c) => sum + (Number(c.kilograms) || 0),
				0,
			);
			const cleaningIds = cleanings.map(c => c._id);

			const existingItemIndex = updatedItems.findIndex(
				item => item._module_vars?.residue_id === residueId,
			);

			if (existingItemIndex !== -1) {
				const existingItem = updatedItems[existingItemIndex];
				const newCollectedKilograms =
					(existingItem._module_vars?.residue_collected_kilograms || 0) +
					cleaningKilograms;
				const newTotalKilograms =
					(existingItem._module_vars?.residue_kilograms || 0) +
					cleaningKilograms;

				updatedItems[existingItemIndex] = {
					...existingItem,
					DscItem: '',
					_module_vars: {
						...existingItem._module_vars,
						residue_collected_kilograms: newCollectedKilograms,
						residue_collected_ids: [
							...(existingItem._module_vars?.residue_collected_ids ||
								[]),
							...cleaningIds,
						],
						kilograms_total: newTotalKilograms,
					},
				};
			} else {
				const unitMeasurement = {
					_id: UNIT_IDS.KILOGRAM,
					code: 'KG',
					name: 'Kilogramo',
				};

				const newItem = {
					NmbItem: residue?.name || 'Residuo',
					DscItem: '',
					QtyItem: cleaningKilograms,
					UnmdItem: unitMeasurement.code,
					PrcItem: 0,
					MontoItem: 0,
					VlrCodigo: residue?.code || '',
					TpoCodigo: '',
					_module_vars: {
						residue_id: residueId,
						residue: residue,
						unit_measurement_id: UNIT_IDS.KILOGRAM,
						unit_measurement: unitMeasurement,
						residue_category_id: residue?.residue_category_id,
						residue_kilograms: 0,
						residue_collected_kilograms: cleaningKilograms,
						residue_collected_ids: cleaningIds,
						kilograms_total: cleaningKilograms,
					},
				};

				updatedItems.push(newItem);
			}
		});

		validation.setFieldValue('items', updatedItems, false);
		toast.success(
			`Se agregaron ${selectedCleanings.length} limpieza(s) de playa exitosamente`,
		);
	};

	return {
		isResiduosModalOpen,
		setIsResiduosModalOpen,
		isBeachCleaningModalOpen,
		setIsBeachCleaningModalOpen,
		isWarehouseStockPickerOpen,
		openWarehouseStockPicker,
		closeWarehouseStockPicker,
		handleConfirmWarehouseStockPick,
		isGuideDetailPickerOpen,
		openGuideDetailPicker,
		closeGuideDetailPicker,
		handleConfirmGuideDetailPick,
		editingResidueDetail,
		editingResidueIndex,
		isResiduos,
		isLimpiezaPlaya,
		isResiduosRegulares,
		isEnvioBodegaPeligrosos,
		isEnvioBodegaNoPeligrosos,
		guideDetailsList,
		currentResidueIds,
		residueItemsCount,
		handleAddResidue,
		handleUpdateResidue,
		handleEditResidue,
		handleCloseResiduosModal,
		handleCloseBeachCleaningModal,
		handleBeachCleaningSelected,
	};
}
